import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Plus, Trash2, Send } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FilterField } from '@/components/ui/FilterBar';
import { Input } from '@/components/form/Input';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatDate } from '@/lib/format';
import { listSisStores } from '@/api/sisStock';
import {
  listSisTransfers,
  availableSisLots,
  createSisTransfer,
  type SisAvailableLot,
  type SisTransferRow,
} from '@/api/sisTransfer';

interface Line {
  itemId: number;
  itemCode: string;
  itemName: string;
  invoiceNo: string;
  remaining: number;
  lotDate: string;
  qty: string;
}

export function SisTransferPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [fromId, setFromId] = useState<number | ''>('');
  const [toId, setToId] = useState<number | ''>('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [pickLot, setPickLot] = useState('');
  const [pickQty, setPickQty] = useState('');

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const stores = storesQ.data ?? [];
  const fromStore = stores.find((s) => s.id === fromId);

  // Destination is limited to the SAME group as the source (and not itself).
  const toOptions = useMemo(
    () =>
      stores
        .filter((s) => fromStore && s.group === fromStore.group && s.id !== fromId)
        .map((s) => ({ value: s.id, label: `${s.name}${s.city ? ` — ${s.city}` : ''}` })),
    [stores, fromStore, fromId],
  );
  const fromOptions = useMemo(
    () => stores.map((s) => ({ value: s.id, label: `${s.name}${s.group ? ` · ${s.group}` : ''}` })),
    [stores],
  );

  const availQ = useQuery({
    queryKey: ['sis-transfer-available', fromId],
    queryFn: () => availableSisLots(Number(fromId)),
    enabled: fromId !== '',
  });
  const lotKey = (l: SisAvailableLot) => `${l.itemId}|${l.invoiceNo}`;
  const lotOptions = useMemo(
    () =>
      (availQ.data ?? []).map((l) => ({
        value: lotKey(l),
        label: `${l.itemName || l.itemCode} · ${l.invoiceNo} · ${l.qtyRemaining} left`,
      })),
    [availQ.data],
  );

  const transfersQ = useQuery({ queryKey: ['sis-transfers'], queryFn: () => listSisTransfers() });

  const save = useMutation({
    mutationFn: () =>
      createSisTransfer({
        fromPartyId: Number(fromId),
        toPartyId: Number(toId),
        remarks: remarks.trim() || undefined,
        items: lines.map((l) => ({ itemId: l.itemId, invoiceNo: l.invoiceNo, qty: Number(l.qty) })),
      }),
    onSuccess: (r) => {
      toast.push(`Transfer ${r.transferNo} created`, 'success');
      qc.invalidateQueries({ queryKey: ['sis-transfers'] });
      qc.invalidateQueries({ queryKey: ['sis-transfer-available'] });
      qc.invalidateQueries({ queryKey: ['sis-stock'] });
      setLines([]);
      setRemarks('');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Transfer failed'), 'error'),
  });

  function addLine() {
    const lot = (availQ.data ?? []).find((l) => lotKey(l) === pickLot);
    if (!lot) return;
    const qty = Number(pickQty);
    if (!(qty > 0)) { toast.push('Enter a quantity', 'error'); return; }
    if (qty > lot.qtyRemaining) { toast.push(`Only ${lot.qtyRemaining} available on ${lot.invoiceNo}`, 'error'); return; }
    if (lines.some((l) => l.itemId === lot.itemId && l.invoiceNo === lot.invoiceNo)) {
      toast.push('That item + invoice is already added', 'error');
      return;
    }
    setLines((p) => [
      ...p,
      { itemId: lot.itemId, itemCode: lot.itemCode, itemName: lot.itemName, invoiceNo: lot.invoiceNo, remaining: lot.qtyRemaining, lotDate: lot.lotDate, qty: pickQty },
    ]);
    setPickLot('');
    setPickQty('');
  }

  const canSave = fromId !== '' && toId !== '' && lines.length > 0 && !save.isPending;

  return (
    <div>
      <SectionHeader
        title="SIS Stock Transfer"      />

      <div className="mb-4 overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl sm:[&_input]:h-8 sm:[&_input]:py-1 sm:[&_input]:text-xs">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-color)] px-4 py-2">
          <span className="flex items-center gap-2 text-sm font-extrabold">
            <ArrowLeftRight size={14} className="text-accent" /> New transfer
          </span>
          <div className="flex items-center gap-3">
            {lines.length > 0 && (
              <span className="text-xs text-[var(--text-muted)]">
                {lines.length} line{lines.length === 1 ? '' : 's'} · {lines.reduce((a, l) => a + Number(l.qty), 0)} pcs
              </span>
            )}
            <Button variant="primary" size="sm" icon={<Send size={13} />} disabled={!canSave} onClick={() => save.mutate()}>
              {save.isPending ? 'Transferring…' : 'Create Transfer'}
            </Button>
          </div>
        </div>

        {/* Stores + remarks — one row, inline labels, like the filter bars. */}
        <div className="grid grid-cols-1 gap-2 px-4 py-3 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
          <FilterField label="From" className="sm:w-[290px]">
            <SearchableSelect
              options={fromOptions}
              value={fromId === '' ? '' : fromId}
              onChange={(v) => { setFromId(v === '' ? '' : Number(v)); setToId(''); setLines([]); setPickLot(''); }}
              placeholder="Search source store…"
            />
          </FilterField>
          <FilterField label="To" className="sm:w-[270px]">
            <SearchableSelect
              options={toOptions}
              value={toId === '' ? '' : toId}
              onChange={(v) => setToId(v === '' ? '' : Number(v))}
              placeholder={fromStore ? 'Search destination…' : 'Pick source first'}
            />
          </FilterField>
          {fromStore?.group && (
            <span title="The destination must be a store in the same group as the source">
              <Badge tone="muted">Same group: {fromStore.group}</Badge>
            </span>
          )}
          <FilterField label="Remarks" className="sm:min-w-[340px] sm:flex-1">
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Challan reference / reason (optional)" />
          </FilterField>
        </div>

        {/* Line picker */}
        <div className="border-t border-[var(--border-color)] px-4 py-3">
          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
            <FilterField label="Add stock" className="sm:w-[560px]">
              <SearchableSelect
                options={lotOptions}
                value={pickLot}
                onChange={(v) => setPickLot(String(v))}
                placeholder={fromId === '' ? 'Pick source store first' : (lotOptions.length ? 'Search item / invoice lot…' : 'No stock at this store')}
              />
            </FilterField>
            <FilterField label="Qty" className="sm:w-[120px]">
              <Input type="number" value={pickQty} onChange={(e) => setPickQty(e.target.value)} placeholder="0" className="text-right" />
            </FilterField>
            <Button variant="glass" size="sm" className="w-full justify-center sm:w-20 sm:shrink-0" icon={<Plus size={13} />} onClick={addLine} disabled={!pickLot || !pickQty}>Add</Button>
          </div>

          {lines.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-wider text-accent">
                    <th className="py-1">Item</th>
                    <th className="py-1">Invoice</th>
                    <th className="py-1">Lot date</th>
                    <th className="py-1 text-right">Qty</th>
                    <th className="py-1 text-right">Avail</th>
                    <th className="py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`${l.itemId}-${l.invoiceNo}`} className="border-t border-[var(--border-color)]/50">
                      <td className="py-1">{l.itemName || l.itemCode}</td>
                      <td className="py-1 font-mono text-xs text-accent">{l.invoiceNo}</td>
                      <td className="py-1 text-[var(--text-muted)]">{formatDate(l.lotDate)}</td>
                      <td className="py-1 text-right font-bold">{l.qty}</td>
                      <td className="py-1 text-right text-[var(--text-muted)]">{l.remaining}</td>
                      <td className="py-1 text-right">
                        <button onClick={() => setLines((p) => p.filter((_, x) => x !== i))} className="rounded p-1 text-[var(--text-muted)] hover:bg-danger/10 hover:text-danger" aria-label="Remove">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Table<SisTransferRow>
        storageKey="sis-transfers"
        paginated
        title="Transfers"
        count={`${transfersQ.data?.length ?? 0}`}
        rows={transfersQ.data ?? []}
        rowKey={(r) => r.id}
        loading={transfersQ.isFetching && !transfersQ.data}
        columns={[
          { key: 'no', header: 'Transfer No', cell: (r) => <span className="font-extrabold text-accent">{r.transferNo}</span> },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.transferDate) },
          { key: 'from', header: 'From', cell: (r) => <span className="font-bold">{r.fromStore}</span> },
          { key: 'to', header: 'To', cell: (r) => <span className="font-bold">{r.toStore}</span> },
          { key: 'group', header: 'Group', cell: (r) => (r.group ? <Badge tone="muted">{r.group}</Badge> : '—') },
          { key: 'items', header: 'Lines', align: 'right', cell: (r) => r.items.length },
          { key: 'qty', header: 'Qty', align: 'right', cell: (r) => <strong>{r.totalQty}</strong> },
          { key: 'inv', header: 'Invoices', cell: (r) => <span className="text-xs text-[var(--text-muted)]">{[...new Set(r.items.map((i) => i.invoiceNo))].join(', ')}</span> },
        ]}
      />
    </div>
  );
}
